import Glide, {
  Controls,
  Swipe,
  Images,
} from "@glidejs/glide/dist/glide.modular.esm";
import { initTranslation } from "../modules/i18n";
import initPage from "../modules/page";
import { stickyElement } from "../modules/ui.js";
import { sendFacebookPixelEvent } from "../modules/analytics/facebook-pixel";
import {
  sendPinterestPageViewEvent,
  sendPinterestAddToCartEvent,
} from "../modules/analytics/pinterest";
import { initCarousels } from "../modules/product-collection";
import { minWidth, styleSecondWord } from "../modules/utilities";
import { initAccordion, initToggleClassLinks } from "../modules/ui.js";
import { buyNowRequest } from "../modules/api";
import { initKlarnaOnsiteMessaging } from "../modules/klarna-messaging";
import { initPayPalMessaging } from "../modules/paypal-messaging.js";
import "../../scss/product.scss";
import { initProductTranslations } from "../modules/product-translations";
import { ReadMoreList, ReadMoreListDevice } from "../modules/read-more-list";
import { handleAddToCart } from "../modules/events/handleAddToCart";
import {
  sendAddToCartEvent,
  sendSelectPromotionEvent,
  sendViewItemEvent,
  sendViewPromotionEvent,
} from "../modules/analytics/google-analytics";
import {
  sendRTBProductPageViewEvent,
  sendRTBAddToCartEvent,
} from "../modules/analytics/rtb-house";
import {
  sendTikTokPageViewEvent,
  sendTikTokAddToCartEvent,
} from "../modules/analytics/tiktok";
import { initCompareSellersMenu } from "../modules/components/side-menu/CompareSellersMenu";
import { initProductTags } from "../modules/product-tags";
import { initProductTabs } from "../modules/product-tabs";
import { initVisualSelect } from "../modules/visual-select";
import { renderGradeDescription } from "../modules/grade-description";
import { initQuantitySelector } from "../modules/quantity-selector";
import { initQuantityOffer } from "../modules/quantity-offer";
import { select, selectAll } from "@fruugo/utilities";

const {
  retailerInformation,
  skuInfo,
  breadcrumbs,
  productId,
  cheapestShippingMethod,
} = window;

let productPillTimer;

const getQuantity = () => {
  const quantityInput = select(".js-quantity-input");

  if (!quantityInput) return 1;

  const quantity = Number(quantityInput.value);

  return quantity;
};

function productPageAddToCart() {
  const { skuId } = document.getElementById("add-to-cart").dataset;

  const quantity = getQuantity();

  handleAddToCart(skuId, quantity);
}

function productPageBuyNow(e) {
  e.preventDefault();

  // disable buttons to avoid double clicks (handles sticky header as well as buybox)
  document.querySelectorAll("button.js-buy-now").forEach((item) => {
    item.disabled = true;
    item.classList.add("btn-loading");
  });

  const { skuId } = document.getElementById("add-to-cart").dataset;

  const quantity = getQuantity();

  buyNowRequest(skuId, quantity)
    .then((response) => {
      if (response.cartValid === false || !response.checkoutUrl) {
        throw new Error();
      }

      if (response.itemAddedToCart) {
        sendAddToCartEvent(
          skuInfo,
          quantity,
          {
            buy_now: true,
          },
          {
            sku_id: skuInfo.id,
            retailer_id: retailerInformation.id,
            retailer_name: retailerInformation.name,
            retailer_country: retailerInformation.country,
            days_to_dispatch: skuInfo.shipping.daysToDispatch,
            days_to_deliver: cheapestShippingMethod?.daysToDelivery,
            shipping_cost: cheapestShippingMethod?.numericPrice,
            free_shipping: cheapestShippingMethod?.free,
            item_available: skuInfo.sellable,
            availability_status: skuInfo.availabilityStatus,
            shipping_from_country: skuInfo?.shipping.originCountryCode,
            item_grade: skuInfo.analyticsData?.grade,
            item_condition: skuInfo.analyticsData?.condition ?? "New",
          }
        );

        sendFacebookPixelEvent("AddToCart", {
          value:
            skuInfo.price.numericTotalInclTaxAfterDiscount ??
            skuInfo.price.numericTotalInclTax,
          currency: skuInfo.price.currencyCode,
          contents: [{ id: skuInfo.productId, quantity }],
          content_type: "product",
        });

        sendRTBAddToCartEvent(skuId);

        sendPinterestAddToCartEvent(
          window.skuInfo.productId,
          window.skuInfo.title,
          window.skuInfo.price.numericTotalInclTaxAfterDiscount ??
            window.skuInfo.price.numericTotalInclTax,
          quantity,
          window.skuInfo.price.currencyCode
        );
        sendTikTokAddToCartEvent(
          window.skuInfo.productId,
          window.skuInfo.title,
          window.skuInfo.price.currencyCode,
          window.skuInfo.description,
          quantity,
          window.skuInfo.price.numericTotalInclTaxAfterDiscount ??
            window.skuInfo.price.numericTotalInclTax
        );

        // Slight delay to allow analytics to send
        setTimeout(() => {
          window.location.href = response.checkoutUrl;
        }, 75);
      } else {
        window.location.href = response.checkoutUrl;
      }
    })
    .catch(() => {
      window.location.href = "/cart";
    });
}

function initProductPageAddToCartEvent() {
  const addToCartButton = selectAll(".js-add-to-cart");
  const buyNowButton = selectAll(".js-buy-now");

  if (addToCartButton) {
    addToCartButton.forEach((button) => {
      button.addEventListener("click", (event) => {
        productPageAddToCart(event);
      });
    });
  }

  if (buyNowButton) {
    buyNowButton.forEach((button) => {
      button.addEventListener("click", (event) => {
        productPageBuyNow(event);
      });
    });
  }
}

function productPageLayout() {
  // Sticky TOC
  const stickyTriggers = document.querySelectorAll(".js-sticky-trigger");
  const bodyRectTop = document.body.getBoundingClientRect().top;

  if (stickyTriggers && stickyTriggers.length) {
    stickyTriggers.forEach((stickyTrigger) => {
      const stickyTriggerRectTop = stickyTrigger.getBoundingClientRect().top;
      minWidth(1280, () => {
        stickyElement(
          ".js-sticky",
          stickyTriggerRectTop - bodyRectTop - 18,
          "d-xl-block"
        );
      });
    });
  }
}

function initImageGalleryZoom(container) {
  const img = document.querySelector(container).querySelector("img");

  document
    .querySelector(container)
    .addEventListener("mouseover", function (event) {
      const imgWidth = img.naturalWidth;
      const box = event.currentTarget;
      const boxClassName = box.getAttribute("class");
      const boxWidth =
        document.getElementsByClassName(boxClassName)[0].offsetWidth;
      if (boxWidth < imgWidth) {
        const x = imgWidth / boxWidth;
        img.style.transform = "scale(" + x + ")";
      } else {
        img.style.cursor = "default";
      }
    });

  document.querySelector(container).addEventListener("mouseout", function () {
    img.style.transform = "scale(1)";
  });

  document
    .querySelector(container)
    .addEventListener("mousemove", function (event) {
      const imgWidth = img.naturalWidth;
      const box = event.currentTarget;
      const boxClassName = box.getAttribute("class");
      const boxElement = document.getElementsByClassName(boxClassName)[0];
      const boxWidth = boxElement.offsetWidth;
      const boxHeight = boxElement.offsetHeight;
      const boxOffsetLeft = boxElement.getBoundingClientRect().left;
      const boxOffsetTop = boxElement.getBoundingClientRect().top;
      if (boxWidth < imgWidth) {
        img.style.transformOrigin =
          ((event.pageX - boxOffsetLeft) / boxWidth) * 100 +
          "%" +
          ((event.pageY - boxOffsetTop) / boxHeight) * 100 +
          "%";
      }
    });
}

function productPageGallery() {
  const productGalleryContainer = document.querySelector(
    ".ProductGallery--carousel"
  );
  const productGalleryItems = productGalleryContainer.querySelectorAll(
    ".ProductGallery__Item"
  );
  const productGalleryNavigation =
    productGalleryContainer.querySelector(".glide__bullets");
  // Mobile
  minWidth(
    992,
    false,
    () => {
      if (productGalleryItems.length > 1) {
        new Glide(productGalleryContainer, {
          perView: 1,
          animationDuration: 600,
          direction: window.rtl === "true" ? "rtl" : "ltr",
        }).mount({ Controls, Swipe, Images });
        productGalleryNavigation.classList.remove("d-none");
      }
    },
    true
  );

  // Desktop
  minWidth(992, () => {
    const container = ".js-hover-zoom";
    if (document.querySelector(".js-gallery-thumbs")) {
      const siblings = document
        .querySelector(".js-gallery-thumbs")
        .querySelectorAll(".js-gallery-thumb");
      siblings.forEach((item) => {
        const url = item.getAttribute("data-image");
        const thumbnailUrl = url.replace("_max", "_0100_0100");
        const thumbnail = new Image();
        thumbnail.src = thumbnailUrl;
        thumbnail.className = "ProductGallery__Thumb__Image";
        thumbnail.setAttribute("onerror", "handleImageError(this)");
        item.appendChild(thumbnail);

        item.addEventListener("click", function (event) {
          event.currentTarget.classList.add("ProductGallery__Thumb--active");
          siblings.forEach((item) => {
            if (event.currentTarget !== item) {
              item.classList.remove("ProductGallery__Thumb--active");
            }
          });

          const url = event.currentTarget.dataset.image;
          document
            .querySelector(container)
            .querySelector("img")
            .setAttribute("src", url);
        });
      });
    }
    initImageGalleryZoom(container);
  });
}

function initPageSpecificAnalytics() {
  sendViewItemEvent(skuInfo, {
    sku_id: skuInfo.id,
    retailer_id: retailerInformation.id,
    retailer_name: retailerInformation.name,
    retailer_country: retailerInformation.country,
    days_to_dispatch: skuInfo.shipping.daysToDispatch,
    days_to_deliver: cheapestShippingMethod?.daysToDelivery,
    shipping_cost: cheapestShippingMethod?.numericPrice,
    free_shipping: cheapestShippingMethod?.free,
    item_available: skuInfo.sellable,
    item_grade: skuInfo.analyticsData?.grade,
    item_condition: skuInfo.analyticsData?.condition ?? "New",
    availability_status: skuInfo.availabilityStatus,
    shipping_from_country: skuInfo?.shipping.originCountryCode,
  });

  sendFacebookPixelEvent("ViewContent", {
    value:
      skuInfo.price.numericTotalInclTaxAfterDiscount ??
      skuInfo.price.numericTotalInclTax,
    currency: skuInfo.price.currencyCode,
    content_ids: [productId],
    content_type: "product",
    content_category: breadcrumbs
      .map((b) => b.text)
      .reduce((current, prev) => `${current} > ${prev}`),
  });

  sendRTBProductPageViewEvent(productId);

  sendPinterestPageViewEvent();
  sendTikTokPageViewEvent(
    skuInfo.productId,
    skuInfo.title,
    skuInfo.price.numericTotalInclTaxAfterDiscount ??
      skuInfo.price.numericTotalInclTax,
    skuInfo.price.currencyCode,
    skuInfo.description
  );

  const headerBanner = document.querySelector("#header-banner");

  if (headerBanner) {
    const { promotionId, promotionName, creativeName, creativeSlot } =
      headerBanner.dataset;

    sendViewPromotionEvent(
      promotionId,
      promotionName,
      creativeName,
      creativeSlot
    );
    headerBanner.addEventListener("click", () => {
      sendSelectPromotionEvent(
        promotionId,
        promotionName,
        creativeName,
        creativeSlot
      );
    });
  }
}

function onConsentUpdate() {
  document.addEventListener("consentUpdate", (e) => {
    const consents = e.detail.consents;
    if (consents && consents.advertising) {
      sendRTBProductPageViewEvent(productId);
    }
  });
}

function productImagePill() {
  const pills = document.querySelectorAll(".js-product-gallery-pill");
  const pillCloseClass = "ProductGallery__Pill--close";
  const pillTimeToFade = 6000;

  if (pills) {
    clearTimeout(productPillTimer);
    pills.forEach((pill) => {
      productPillTimer = setTimeout(() => {
        pill.classList.add(pillCloseClass);
      }, pillTimeToFade);

      const pillCloseButton = pill.querySelector(".js-product-pill-close");

      if (pillCloseButton) {
        pillCloseButton.addEventListener("click", () => {
          pill.classList.add(pillCloseClass);
        });
      }

      pill.addEventListener("animationend", (event) => {
        if (event.animationName === "move-down") {
          pill.classList.add("d-none");
        }
      });
    });
  }
}

async function initProductPage() {
  await initTranslation();
  initPage();
  productPageLayout();
  initVisualSelect();
  productPageGallery();
  initProductPageAddToCartEvent();
  initCarousels();
  initProductTranslations();
  initPageSpecificAnalytics();
  initAccordion();
  initToggleClassLinks();
  productImagePill();
  initKlarnaOnsiteMessaging();
  initPayPalMessaging();
  initProductTags();
  onConsentUpdate();
  initCompareSellersMenu();
  initProductTabs();
  renderGradeDescription();
  initQuantitySelector(skuInfo.sellableQuantity);
  initQuantityOffer();

  const readMoreList = new ReadMoreList(
    ".js-read-more-list",
    ReadMoreListDevice.MobileOnly
  );
  readMoreList.init();
  styleSecondWord(document.querySelector(".js-style-second-word"));
}

initProductPage();
